import type {ProductManualDocument} from '@/types/sanity'
import {manualGroupTitle} from '@/components/products/productDisplay'
import {ProductDocumentsSheet} from '@/components/products/ProductDocumentsSheet'
import {ProductCard} from '@/components/products/ProductCard'
import type {ProductDocument} from '@/types/sanity'
import {isProductDiscontinued} from '@/components/products/productDisplay'
import {t} from '@/lib/i18n'
import {ChevronDown} from 'lucide-react'

export type ManualsGridItem = {
  key: string
  product: ProductDocument
  documents: ProductManualDocument[]
  loadDocuments?: (signal?: AbortSignal) => Promise<ProductManualDocument[]>
  productLinks?: ProductMarketLink[]
}

export type ProductMarketLink = {
  label: string
  href: string
}

type ManualsGridProps = {
  items: ManualsGridItem[]
  locale?: string
  filterDocumentsByLocale?: boolean
  resolveProductHref?: (product: ProductDocument) => string
  discontinuedLabel?: string
}

const normalizeProductNumber = (value?: string) =>
  String(value || '')
    .replace(/[^\x20-\x7E]/g, '')
    .trim()
    .toUpperCase()

function ProductMarketLinksDropdown({label, links}: {label: string; links: ProductMarketLink[]}) {
  if (!links.length) return null

  return (
    <details className="group/market relative w-full text-center">
      <summary className="inline-flex cursor-pointer list-none items-center justify-center gap-1 u-body-sm font-semibold text-foreground underline-offset-2 hover:underline [&::-webkit-details-marker]:hidden">
        {label}
        <ChevronDown className="size-4 transition-transform group-open/market:rotate-180" />
      </summary>
      <div className="absolute bottom-full left-1/2 z-20 mb-2 min-w-44 -translate-x-1/2 rounded-card border border-border bg-white py-2 text-left shadow-lg">
        {links.map((link) => (
          <a
            key={`${link.href}-${link.label}`}
            href={link.href}
            className="block px-4 py-2 text-sm font-semibold text-foreground hover:bg-muted"
          >
            {link.label}
          </a>
        ))}
      </div>
    </details>
  )
}

export function ManualsGrid({
  items,
  locale,
  filterDocumentsByLocale = true,
  resolveProductHref,
  discontinuedLabel,
}: ManualsGridProps) {
  const resolvedDiscontinuedLabel =
    discontinuedLabel ?? t(locale, 'product.discontinued', 'Product discontinued')
  const seeProductLabel = t(locale, 'product.seeProduct', 'See product')

  return (
    <div className="grid grid-cols-1 gap-card-gap md:grid-cols-2 xl:grid-cols-3">
      {items.map((item, index) => {
        const productNumber = normalizeProductNumber(item.product.productNumber)
        const manualSheetId = `manual-docs-${productNumber || item.product._id || index}`
        const discontinued = isProductDiscontinued(item.product)
        const productHref = resolveProductHref ? resolveProductHref(item.product) : '#'
        const productLinks = item.productLinks || []

        return (
          <ProductCard
            key={item.key}
            product={item.product}
            href={productHref}
            locale={locale}
            ctaLabel={productLinks.length ? undefined : seeProductLabel}
            ctaVariant="text"
            ctaLayout="column"
            statusLabel={discontinued ? resolvedDiscontinuedLabel : undefined}
            deemphasized={discontinued}
            hideProductPrices
            showHighlights={false}
            showDescription={false}
            secondaryCta={
              <>
                <ProductDocumentsSheet
                  sheetId={manualSheetId}
                  title={manualGroupTitle(item.documents)}
                  documents={item.documents}
                  loadDocuments={item.loadDocuments}
                  locale={locale}
                  filterByLocale={filterDocumentsByLocale}
                />
                <ProductMarketLinksDropdown label={seeProductLabel} links={productLinks} />
              </>
            }
          />
        )
      })}
    </div>
  )
}
